import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import type { ComplianceCard } from '@/api/client';
import { ClipboardList, Download, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/** 后端不可用（未登录/离线）时，用当前展示数据在本地生成报告 */
function buildLocalReport(c: ComplianceCard): Blob {
  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><title>${c.frameworkCode} Compliance Report</title>
<style>body{font-family:'Segoe UI',Arial,sans-serif;color:#16263a;margin:40px;max-width:880px}
h1{border-bottom:3px solid #0d6e5e;padding-bottom:8px}
.meta{color:#5d7187;font-size:14px;margin-bottom:24px}
.badge{display:inline-block;background:#0d6e5e;color:#fff;border-radius:4px;padding:2px 10px;font-size:13px}
.kpi{display:inline-block;margin-right:32px;font-size:15px}
.kpi b{font-size:24px;display:block;color:#0d6e5e}
footer{margin-top:36px;color:#8195aa;font-size:12px;border-top:1px solid #cdd9e5;padding-top:12px}</style>
</head><body>
<h1>${c.frameworkName} — Compliance Report</h1>
<div class="meta">${c.description}<br>Generated: ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC
&nbsp;·&nbsp; <span class="badge">AI Security &amp; Compliance Hub</span></div>
<div>
  <span class="kpi"><b>${c.openViolations}</b>Open Violations</span>
  <span class="kpi"><b>${c.status}</b>Report Status</span>
  <span class="kpi"><b>${c.lastGeneratedAt ?? 'N/A'}</b>Last Generated</span>
</div>
<footer>This report was generated by AI Security &amp; Compliance Hub. Framework: ${c.frameworkCode}.</footer>
</body></html>`;
  return new Blob([html], { type: 'text/html' });
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ComplianceReportingHub({ cards }: { cards: ComplianceCard[] }) {
  const [busy, setBusy] = useState<string | null>(null);

  const download = async (c: ComplianceCard) => {
    if (busy) return;
    setBusy(c.frameworkCode);
    try {
      let blob: Blob;
      try {
        blob = await api.downloadReport(c.frameworkCode);
      } catch {
        blob = buildLocalReport(c);
      }
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      saveBlob(blob, `${c.frameworkCode}-compliance-report-${date}.html`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card dashboard-panel h-full p-2.5">
      <div className="dashboard-title mb-2 flex items-center gap-2 leading-tight">
        <ClipboardList className="w-[18px] h-[18px]" /> Compliance Reporting Hub
      </div>

      <div className="space-y-2">
        {cards.map((c) => (
          <div
            key={c.frameworkCode}
            className={cn(
              'rounded-md px-2 py-2 border border-[#19395a] bg-[#071d31] flex items-center justify-between gap-2 min-h-[55px]',
            )}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <ReportMark code={c.frameworkCode} />
              <div className="min-w-0">
                <div className="font-bold text-[11px] text-[#f3f7fb] truncate">{c.frameworkName}</div>
                <div className="flex items-center gap-1.5 mt-1 text-[9px] text-[#879bb2] truncate">
                  <span className="inline-block h-2.5 w-[40px] rounded-full bg-good" />
                  {reportMeta(c)}
                </div>
              </div>
            </div>

            <button
              onClick={() => download(c)}
              disabled={busy === c.frameworkCode}
              className="inline-flex min-w-[54px] items-center justify-center gap-1 bg-transparent hover:bg-[#0b2035] border border-[#18a780]/70 text-[#bfeadf] text-[7px] font-medium px-1 py-1 rounded transition disabled:opacity-50"
            >
              {busy === c.frameworkCode
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Download className="w-3 h-3" />}
              <span>Download<br />Report</span>
            </button>
          </div>
        ))}
      </div>

      <Link
        to="/compliance-reports"
        className="block mt-2 text-[11px] text-[#58a6ff] text-center hover:underline"
      >
        View All Compliance Reports &rarr;
      </Link>
    </div>
  );
}

function ReportMark({ code }: { code: string }) {
  const label = code === 'SOC2' ? 'SOC' : code === 'GDPR' ? 'EU' : code.replace('ISO', 'ISO ');
  return (
    <div className="h-[30px] w-[30px] shrink-0 rounded-sm border border-[#284b70] bg-[#e9f3f7] grid place-items-center text-center text-[7px] font-black leading-none text-[#18628b]">
      {label}
    </div>
  );
}

function reportMeta(card: ComplianceCard) {
  if (card.frameworkCode === 'SOC2') return 'Last Audit: Jan 2026';
  if (card.frameworkCode === 'GDPR') return `${card.openViolations} Open Violations`;
  if (card.frameworkCode === 'HIPAA') return 'BAA Available';
  if (card.frameworkCode === 'ISO27017') return 'Cloud Security Controls';
  if (card.frameworkCode === 'ISO27018') return 'PII in Public Cloud';
  return `Scope: ${card.description}`;
}
